// LanguageSwitcher.tsx
import React from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contextProviders/AuthProvider';
import { useUserProfile } from '../contextProviders/UserProfileProvider';
import { updateMyUserProfile } from '../services/UserProfileService';
import { APP_LANGUAGE_OPTIONS, resolveAppLanguage } from '../constants/appLanguages';

/**
 * Global header language picker (both authenticated and public layouts).
 * Signed-in users with a completed UserProfile get PreferredLanguage
 * persisted there so it follows them across devices/browsers instead of
 * only living in i18next-browser-languagedetector's local cache.
 * Signed-out visitors (or accounts still on /complete-profile) just get
 * the local i18n switch, same as before.
 *
 * The languages offered live in APP_LANGUAGES (constants/appLanguages.ts),
 * shared with Settings > Language and /complete-profile's own picker.
 */
const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { hasUserProfile, refresh } = useUserProfile();

  const handleLanguageChange = async (lang: string) => {
    i18n.changeLanguage(lang);
    if (isAuthenticated && hasUserProfile) {
      try {
        await updateMyUserProfile({ preferredLanguage: lang });
        await refresh();
      } catch (err) {
        console.error('Failed to persist preferred language:', err);
      }
    }
  };

  return (
    <Select
      // resolveAppLanguage, not i18n.language raw: the browser detector
      // reports the full tag ("sk-SK", "en-GB"), which matches none of the
      // options and leaves the control showing that tag instead of the
      // language's name -- even though the app IS in that language.
      value={resolveAppLanguage(i18n.resolvedLanguage ?? i18n.language)}
      style={{ width: 130 }}
      className="heavyDutyMachinery-language-switcher"
      onChange={handleLanguageChange}
      options={APP_LANGUAGE_OPTIONS}
    />
  );
};

export default LanguageSwitcher;
